import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Evaluation } from '@/types/evaluation.types';
import { canActivateEvaluation } from '@/services/evaluation-status';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, Play } from 'lucide-react';

interface ActivateDialogProps {
  evaluation: Evaluation;
  onActivate: (evaluation: Evaluation) => void;
  children: React.ReactNode;
}

export function ActivateDialog({
  evaluation,
  onActivate,
  children,
}: ActivateDialogProps) {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);

  const validation = canActivateEvaluation(evaluation);
  const { isValid } = validation;

  const handleActivate = () => {
    if (isValid) {
      onActivate(evaluation);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-green-600" aria-hidden="true" />
            {t('status.activate.title')}
          </DialogTitle>
          <DialogDescription>{t('status.activate.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Requirements checklist */}
          <div className="space-y-3">
            <p className="text-sm font-medium">{t('status.activate.requirements')}</p>
            <div className="space-y-2">
              {/* Solutions check */}
              <div className="flex items-start gap-2">
                {evaluation.targetSolutions.length > 0 ? (
                  <CheckCircle2
                    className="h-4 w-4 mt-0.5 text-green-600 dark:text-green-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <AlertCircle
                    className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span className="text-sm">
                  {evaluation.targetSolutions.length > 0
                    ? `${evaluation.targetSolutions.length} solution(s) configured`
                    : t('status.activate.blocker.noSolutions')}
                </span>
              </div>

              {/* Criteria groups check */}
              <div className="flex items-start gap-2">
                {evaluation.criteriaGroups.length > 0 ? (
                  <CheckCircle2
                    className="h-4 w-4 mt-0.5 text-green-600 dark:text-green-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <AlertCircle
                    className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span className="text-sm">
                  {evaluation.criteriaGroups.length > 0
                    ? `${evaluation.criteriaGroups.length} criteria group(s) configured`
                    : t('status.activate.blocker.noGroups')}
                </span>
              </div>

              {/* Criteria check */}
              {(() => {
                const totalCriteria = evaluation.criteriaGroups.reduce(
                  (sum, group) => sum + group.criteria.length,
                  0
                );
                return (
                  <div className="flex items-start gap-2">
                    {totalCriteria > 0 ? (
                      <CheckCircle2
                        className="h-4 w-4 mt-0.5 text-green-600 dark:text-green-400 flex-shrink-0"
                        aria-hidden="true"
                      />
                    ) : (
                      <AlertCircle
                        className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <span className="text-sm">
                      {totalCriteria > 0
                        ? `${totalCriteria} criteria configured`
                        : t('status.activate.blocker.noCriteria')}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Validation message */}
          {!isValid && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-3">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                Please complete the requirements above before activating.
              </p>
            </div>
          )}

          {isValid && (
            <div className="rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 p-3">
              <p className="text-sm text-green-900 dark:text-green-200">
                All requirements met! Ready to activate.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleActivate} disabled={!isValid}>
            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('status.activate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
